import { useMemo } from 'react';
import { useField, useForm } from '@shopify/react-form';
import round from 'lodash/round';
import { AutomatedBreaksSettingsUI } from '@sdk/automatedBreaks/types/AutomatedBreaksSettingsUI';
import gettext from '@sdk/lang/gettext';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import {
  AutomatedBreaksWaivingMode,
  AutomatedBreaksWaivingBreakMode,
} from '@sdk/authentication/types/apiTypes';

import {
  minutesValidator,
  hoursValidator,
  breakDurationLessThanBreakThresholdValidator,
  breakThresholdGreaterThanBreakDurationValidator,
  hoursThatMapToWholeNumberOfMinutesValidators,
  validateIsNumberMultipleOfHalfHour,
} from '@web/settings/components/schedule/Breaks/breaks.utils';

export const CANNOT_BE_MORE_THAN_WAIVER_ERROR = 'CANNOT_BE_MORE_THAN_WAIVER_ERROR';

const useBreaksForm = (automatedBreaksSettings: AutomatedBreaksSettingsUI) => {
  const defaultValues = useMemo(() => automatedBreaksSettings, [automatedBreaksSettings]);
  const { fields } = useForm({
    fields: {
      isEnabled: useField(defaultValues.isEnabled),
      isAttestation: useField(defaultValues.isAttestation),
      meal: {
        isEnabled: useField(defaultValues.meal.isEnabled),
        isPaid: useField(defaultValues.meal.isPaid),
        breakDuration: useField({
          value: String(defaultValues.meal.breakDuration),
          validates: [
            (val) => minutesValidator(val),
            (val) =>
              breakDurationLessThanBreakThresholdValidator(
                Number(val),
                automatedBreaksSettings.meal.breakThreshold
              ),
          ],
        }),
        breakThreshold: useField(
          {
            value: String(round(defaultValues.meal.breakThreshold / 60, 2)),
            validates: [
              (val) => hoursValidator(val),
              (val) =>
                breakThresholdGreaterThanBreakDurationValidator(
                  round(Number(val) * 60),
                  automatedBreaksSettings.meal.breakDuration
                ),
              (val) => validateIsNumberMultipleOfHalfHour(Number(val)),
              (val) => {
                const shouldCheckFirstBreakLessThanHoursCondition =
                  automatedBreaksSettings.meal.isWaivingEnabled &&
                  automatedBreaksSettings.meal.waivingMode !==
                    AutomatedBreaksWaivingMode.SECOND_BREAK_ONLY &&
                  automatedBreaksSettings.meal.waivingFirstBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS;

                const shouldCheckSecondBreakLessThanHoursCondition =
                  automatedBreaksSettings.meal.isWaivingEnabled &&
                  automatedBreaksSettings.meal.waivingMode !==
                    AutomatedBreaksWaivingMode.FIRST_BREAK_ONLY &&
                  automatedBreaksSettings.meal.waivingSecondBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS;

                if (
                  (shouldCheckFirstBreakLessThanHoursCondition &&
                    Number(val) > automatedBreaksSettings.meal.waivingFirstBreakLessThanHours) ||
                  (shouldCheckSecondBreakLessThanHoursCondition &&
                    Number(val) > 2 * automatedBreaksSettings.meal.waivingSecondBreakLessThanHours)
                ) {
                  return CANNOT_BE_MORE_THAN_WAIVER_ERROR;
                }
              },
            ],
          },
          [
            automatedBreaksSettings.meal.isWaivingEnabled,
            automatedBreaksSettings.meal.waivingMode,
            automatedBreaksSettings.meal.waivingFirstBreakMode,
            automatedBreaksSettings.meal.waivingFirstBreakLessThanHours,
            automatedBreaksSettings.meal.waivingSecondBreakMode,
            automatedBreaksSettings.meal.waivingSecondBreakLessThanHours,
          ]
        ),
        isEarlyReturn: useField(defaultValues.meal.isEarlyReturn),
        isPremiumPay: useField(defaultValues.meal.isPremiumPay),
        isWaivingEnabled: useField(defaultValues.meal.isWaivingEnabled),
        waivingMode: useField(defaultValues.meal.waivingMode),
        waivingFirstBreakMode: useField(defaultValues.meal.waivingFirstBreakMode),
        waivingSecondBreakMode: useField(defaultValues.meal.waivingSecondBreakMode),
        waivingFirstBreakLessThanHours: useField(
          {
            value: String(defaultValues.meal.waivingFirstBreakLessThanHours),
            validates: [
              ...hoursThatMapToWholeNumberOfMinutesValidators({
                minValue: automatedBreaksSettings.meal.breakThreshold / 60,
                skipValidation:
                  automatedBreaksSettings.meal.waivingFirstBreakMode ===
                    AutomatedBreaksWaivingBreakMode.ALWAYS ||
                  automatedBreaksSettings.meal.waivingMode ===
                    AutomatedBreaksWaivingMode.SECOND_BREAK_ONLY,
              }),
              (val) => {
                if (
                  automatedBreaksSettings.meal.waivingMode ===
                    AutomatedBreaksWaivingMode.FIRST_AND_SECOND_BREAK &&
                  automatedBreaksSettings.meal.waivingFirstBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                  automatedBreaksSettings.meal.waivingSecondBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                  Number(val) > automatedBreaksSettings.meal.waivingSecondBreakLessThanHours
                ) {
                  return gettext('Cannot be more than the value for the second break');
                }
              },
            ],
          },
          [
            automatedBreaksSettings.meal.waivingMode,
            automatedBreaksSettings.meal.waivingFirstBreakMode,
            automatedBreaksSettings.meal.waivingFirstBreakLessThanHours,
            automatedBreaksSettings.meal.waivingSecondBreakMode,
            automatedBreaksSettings.meal.waivingSecondBreakLessThanHours,
            automatedBreaksSettings.meal.breakThreshold,
          ]
        ),
        waivingSecondBreakLessThanHours: useField(
          {
            value: String(defaultValues.meal.waivingSecondBreakLessThanHours),
            validates: [
              ...hoursThatMapToWholeNumberOfMinutesValidators({
                minValue: (2 * automatedBreaksSettings.meal.breakThreshold) / 60,
                maxValue: 48,
                skipValidation:
                  automatedBreaksSettings.meal.waivingSecondBreakMode ===
                    AutomatedBreaksWaivingBreakMode.ALWAYS ||
                  automatedBreaksSettings.meal.waivingMode ===
                    AutomatedBreaksWaivingMode.FIRST_BREAK_ONLY,
              }),
              (val) => {
                if (
                  automatedBreaksSettings.meal.waivingMode ===
                    AutomatedBreaksWaivingMode.FIRST_AND_SECOND_BREAK &&
                  automatedBreaksSettings.meal.waivingFirstBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                  automatedBreaksSettings.meal.waivingSecondBreakMode ===
                    AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                  Number(val) < automatedBreaksSettings.meal.waivingFirstBreakLessThanHours
                ) {
                  return gettext('Cannot be less than the value for the first break');
                }
              },
            ],
          },
          [
            automatedBreaksSettings.meal.waivingMode,
            automatedBreaksSettings.meal.waivingFirstBreakMode,
            automatedBreaksSettings.meal.waivingFirstBreakLessThanHours,
            automatedBreaksSettings.meal.waivingSecondBreakMode,
            automatedBreaksSettings.meal.waivingSecondBreakLessThanHours,
            automatedBreaksSettings.meal.breakThreshold,
          ]
        ),
      },
      rest: {
        isEnabled: useField(defaultValues.rest.isEnabled),
        isPaid: useField(defaultValues.rest.isPaid),
        breakDuration: useField({
          value: String(defaultValues.rest.breakDuration),
          validates: [
            (val) => minutesValidator(val),
            (val) =>
              breakDurationLessThanBreakThresholdValidator(
                Number(val),
                automatedBreaksSettings.rest.breakThreshold
              ),
          ],
        }),
        breakThreshold: useField({
          value: String(round(defaultValues.rest.breakThreshold / 60, 2)),
          validates: [
            (val) => hoursValidator(val),
            (val) =>
              breakThresholdGreaterThanBreakDurationValidator(
                round(Number(val) * 60),
                automatedBreaksSettings.rest.breakDuration
              ),
            (val) => validateIsNumberMultipleOfHalfHour(Number(val)),
          ],
        }),
        isEarlyReturn: useField(defaultValues.rest.isEarlyReturn),
        isPremiumPay: useField(defaultValues.rest.isPremiumPay),
        isWaivingEnabled: useField(defaultValues.rest.isWaivingEnabled),
      },
    },
  });

  return {
    fields,
  };
};

export default useBreaksForm;
